import { Badge, Button } from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { useMatterInsuranceVehiclePermissions } from "@/hooks/useMatterInsuranceVehiclePermissions";
import type { Matter, MatterInsurance } from "@/lib/types";
import {
  getInsuranceContactConnections,
  getInsuranceConnectionContactName,
} from "@/lib/utils/connections/insurance-contact-connections";
import {
  getMatterContactConnections,
  getThirdPartyMatterContactIds,
} from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { cn } from "@/lib/utils/cn";
import { formatInsuranceTitle } from "@/lib/utils/format";
import { ChevronDown, Pencil } from "lucide-react";
import {
  formatCurrency,
  formatLiabilityStatusLabel,
  getInsuranceCoverageStatusLabel,
  getInvestigationFirstPartyInsurances,
  getInvestigationThirdPartyInsurances,
  getLiabilityToneClass,
  getStatusToneClass,
} from "../matter-overview-utils";
import { InsurancePolicyHoverCard } from "./InsurancePolicyHoverCard";

type PolicyParty = "1st Party" | "3rd Party";

type PolicyRow = {
  faultStatus?: string | null;
  insurance: MatterInsurance;
  insured: string;
  insuredKey: string;
  insurer: string;
  limitLines: Array<{ label?: string; value: string }>;
  party: PolicyParty;
  partyTab: "client" | "defendant";
};

function toFiniteNumber(value: unknown): number | null {
  if (value == null || (typeof value === "string" && value.trim() === "")) {
    return null;
  }

  const normalizedValue = Number(value);
  return Number.isFinite(normalizedValue) ? normalizedValue : null;
}

function sumLimits(values: Array<number | null | undefined>) {
  const definedValues = values
    .map(toFiniteNumber)
    .filter((value): value is number => value != null);

  return definedValues.length > 0
    ? definedValues.reduce((total, value) => total + value, 0)
    : null;
}

const compactCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

function formatCompactCurrency(value: number | null) {
  if (value == null || !Number.isFinite(value)) return "—";
  return compactCurrencyFormatter.format(value).replace("K", "k");
}

function formatLimitPair(
  perPerson: number | null | undefined,
  perIncident: number | null | undefined,
) {
  const values = [perPerson, perIncident]
    .map(toFiniteNumber)
    .filter((value): value is number => value != null);

  return values.length > 0 ? values.map(formatCurrency).join("/") : "—";
}

function getPolicyParties(matter: Matter, insurance: MatterInsurance) {
  const insuranceTitle = formatInsuranceTitle({ insurance });
  const insuredConnections = getInsuranceContactConnections(
    insurance,
    "Insured",
  );
  const defendantConnection = getMatterContactConnections(matter).find(
    (connection) =>
      connection.label === "Defendant" &&
      insuredConnections.some(
        (insured) => insured.targetObjectId === connection.contactId,
      ),
  );

  return {
    faultStatus: defendantConnection?.faultStatus,
    insured:
      insuredConnections
        .map((connection) =>
          getInsuranceConnectionContactName(connection.contact),
        )
        .join(", ") || "Unknown insured",
    insuredKey:
      [
        ...new Set(
          insuredConnections.map((connection) => connection.targetObjectId),
        ),
      ]
        .sort()
        .join(",") || insurance.id,
    insurer: insuranceTitle?.insurerPart ?? "Unknown insurer",
  };
}

function groupPolicyRows(rows: PolicyRow[]) {
  const groups = new Map<string, PolicyRow[]>();

  rows.forEach((row) => {
    const key = `${row.party}:${row.insuredKey}`;
    const groupRows = groups.get(key);
    if (groupRows) {
      groupRows.push(row);
    } else {
      groups.set(key, [row]);
    }
  });

  return Array.from(groups, ([key, groupRows]) => ({ key, rows: groupRows }));
}

function getFirstPartyLimitLines(insurance: MatterInsurance) {
  const lines: PolicyRow["limitLines"] = [];

  if (insurance.medPay || insurance.medPayLimit != null) {
    lines.push({
      label: "MedPay",
      value:
        insurance.medPayLimit == null
          ? "Missing"
          : formatCurrency(insurance.medPayLimit),
    });
  }
  if (insurance.umExists || insurance.umPerPerson || insurance.umPerIncident) {
    lines.push({
      label: "UM",
      value: formatLimitPair(insurance.umPerPerson, insurance.umPerIncident),
    });
  }
  if (
    insurance.uimExists ||
    insurance.uimPerPerson ||
    insurance.uimPerIncident
  ) {
    lines.push({
      label: "UIM",
      value: formatLimitPair(insurance.uimPerPerson, insurance.uimPerIncident),
    });
  }

  return lines.length > 0 ? lines : [{ value: "—" }];
}

function buildPolicyRows(matter: Matter): PolicyRow[] {
  const buildRow = (
    insurance: MatterInsurance,
    party: PolicyParty,
  ): PolicyRow => {
    const parties = getPolicyParties(matter, insurance);
    const isThirdParty = party === "3rd Party";

    return {
      ...parties,
      insurance,
      limitLines: isThirdParty
        ? [
            {
              label: "BI",
              value: formatLimitPair(
                insurance.policyLimitPerPerson,
                insurance.policyLimitPerIncident,
              ),
            },
          ]
        : getFirstPartyLimitLines(insurance),
      party,
      partyTab: isThirdParty ? "defendant" : "client",
    };
  };

  return [
    ...getInvestigationThirdPartyInsurances(matter).map((insurance) =>
      buildRow(insurance, "3rd Party"),
    ),
    ...getInvestigationFirstPartyInsurances(matter).map((insurance) =>
      buildRow(insurance, "1st Party"),
    ),
  ];
}

function StatusValue({
  label,
  className,
}: {
  label: string;
  className: string;
}) {
  if (label === "-") {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 font-semibold",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="h-1.5 w-1.5 rounded-full bg-current"
      />
      {label}
    </span>
  );
}

function NoInsuranceNotices({
  insurances,
  contactIds,
}: {
  insurances: MatterInsurance[];
  contactIds: Set<string>;
}) {
  const notices = insurances.flatMap((insurance) => {
    const partyNames = getInsuranceContactConnections(insurance, "Insured")
      .filter((connection) => contactIds.has(connection.targetObjectId))
      .map((connection) =>
        getInsuranceConnectionContactName(connection.contact),
      );

    return partyNames.length > 0 ? [{ insurance, partyNames }] : [];
  });

  if (notices.length === 0) return null;

  return (
    <ul className="mt-2 space-y-1 text-[11px]">
      {notices.map(({ insurance, partyNames }) => (
        <li key={insurance.id}>
          <span className="font-semibold">No {insurance.type} insurance</span>
          <span className="text-muted-foreground">
            {` · ${partyNames.join(", ")}`}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function InsuranceLiabilityTopStat({
  matter,
  isExpanded,
  onExpandedChange,
  className,
}: {
  matter: Matter;
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  className?: string;
}) {
  const { openInsuranceModal } = useGlobalModal();
  const { hasUpdateInsurancePermission } =
    useMatterInsuranceVehiclePermissions();
  const thirdPartyInsurances = getInvestigationThirdPartyInsurances(matter);
  const firstPartyInsurances = getInvestigationFirstPartyInsurances(matter);
  const noInsuranceMarkers = matter.insurances.filter(
    (insurance) =>
      insurance.coverageEnabled === false && insurance.type !== "Health",
  );
  const firstPartyContactIds = new Set(
    getMatterContactConnections(matter)
      .filter((connection) => connection.label === "Client")
      .map((connection) => connection.contactId),
  );
  const thirdPartyContactIds = getThirdPartyMatterContactIds(matter);
  const rows = buildPolicyRows(matter);
  const policyGroups = groupPolicyRows(rows);
  const thirdPartyPerPersonTotal = sumLimits(
    thirdPartyInsurances.map((insurance) => insurance.policyLimitPerPerson),
  );
  const thirdPartyPerIncidentTotal = sumLimits(
    thirdPartyInsurances.map((insurance) => insurance.policyLimitPerIncident),
  );
  const medPayTotal = sumLimits(
    firstPartyInsurances.map((insurance) => insurance.medPayLimit),
  );
  const umTotal = sumLimits(
    firstPartyInsurances.map((insurance) => insurance.umPerPerson),
  );
  const uimTotal = sumLimits(
    firstPartyInsurances.map((insurance) => insurance.uimPerPerson),
  );
  const firstPartySummary = [
    medPayTotal != null ? `MedPay ${formatCurrency(medPayTotal)}` : null,
    umTotal != null ? `UM ${formatCurrency(umTotal)}` : null,
    uimTotal != null ? `UIM ${formatCurrency(uimTotal)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section className={cn("min-w-0 border-r px-4 py-2.5", className)}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-muted-foreground text-[10px] font-semibold">
          Insurance &amp; liability
        </p>
        {rows.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            className="text-muted-foreground hover:text-foreground h-auto shrink-0 px-0 py-0 text-[10px] font-medium hover:bg-transparent"
            onClick={() => {
              onExpandedChange(!isExpanded);
            }}
            aria-expanded={isExpanded}
          >
            {isExpanded ? "Show less" : "Show more"}
            <ChevronDown
              className={cn(
                "ml-0.5 h-3 w-3 transition-transform",
                isExpanded && "rotate-180",
              )}
            />
          </Button>
        ) : null}
      </div>

      <div className="mt-1.5 grid max-w-xl grid-cols-2 gap-5">
        <div className="min-w-0">
          <p className="text-muted-foreground text-[11px]">
            3rd party · limits
          </p>
          <p className="text-foreground text-[15px] leading-snug font-semibold">
            {`${formatCompactCurrency(thirdPartyPerPersonTotal)}/${formatCompactCurrency(thirdPartyPerIncidentTotal)}`}
          </p>
          <p className="text-muted-foreground text-[11px]">
            {`${thirdPartyInsurances.length} ${thirdPartyInsurances.length === 1 ? "policy" : "policies"}`}
          </p>
          <NoInsuranceNotices
            insurances={noInsuranceMarkers}
            contactIds={thirdPartyContactIds}
          />
        </div>
        <div className="min-w-0">
          <p className="text-muted-foreground text-[11px]">
            1st party · limits
          </p>
          <p className="text-foreground text-[15px] leading-snug font-semibold">
            {`${firstPartyInsurances.length} ${firstPartyInsurances.length === 1 ? "policy" : "policies"}`}
          </p>
          <p className="text-muted-foreground line-clamp-2 text-[11px]">
            {firstPartySummary || "No UM/UIM/MedPay limits"}
          </p>
          <NoInsuranceNotices
            insurances={noInsuranceMarkers}
            contactIds={firstPartyContactIds}
          />
        </div>
      </div>

      {isExpanded && rows.length > 0 ? (
        <div className="mt-3 border-t pt-3">
          <table className="w-full table-fixed text-left text-[11px]">
            <thead className="text-muted-foreground text-[9px] font-semibold">
              <tr className="border-b">
                <th className="w-[28%] px-1 py-2 font-semibold">Party</th>
                <th className="w-[18%] px-1 py-2 font-semibold">Insurer</th>
                <th className="w-[23%] px-1 py-2 font-semibold">
                  Policy limits
                </th>
                <th className="w-[16%] px-1 py-2 font-semibold">Coverage</th>
                <th className="w-[15%] px-1 py-2 font-semibold">Liability</th>
              </tr>
            </thead>
            <tbody>
              {policyGroups.flatMap((group) =>
                group.rows.map((row, rowIndex) => {
                  const coverage = getInsuranceCoverageStatusLabel(
                    row.insurance,
                  );
                  const liability = formatLiabilityStatusLabel(row.insurance);
                  const limitLabel = row.limitLines
                    .map(({ label, value }) =>
                      label ? `${label} ${value}` : value,
                    )
                    .join(" · ");
                  const faultStatus = group.rows.find(
                    (groupRow) =>
                      groupRow.faultStatus === "At Fault" ||
                      groupRow.faultStatus === "Not At Fault",
                  )?.faultStatus;

                  return (
                    <tr
                      key={`${row.party}:${row.insurance.id}`}
                      className="border-b last:border-b-0"
                    >
                      {rowIndex === 0 ? (
                        <td
                          rowSpan={group.rows.length}
                          className="px-1 py-3 align-top"
                        >
                          <span className="line-clamp-2 font-semibold">
                            {row.insured}
                          </span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            <Badge
                              variant="secondary"
                              className="h-4 px-1.5 text-[8px] font-medium"
                            >
                              {row.party}
                            </Badge>
                            {faultStatus ? (
                              <Badge
                                className={cn(
                                  "h-4 px-1.5 text-[8px] font-semibold tracking-[0.08em] uppercase",
                                  faultStatus === "At Fault" &&
                                    "bg-red-100 text-red-700 hover:bg-red-100",
                                  faultStatus === "Not At Fault" &&
                                    "bg-slate-100 text-slate-600 hover:bg-slate-100",
                                )}
                              >
                                {faultStatus}
                              </Badge>
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                      <td className="text-muted-foreground px-1 py-3 align-top">
                        <div className="flex min-w-0 items-start gap-1">
                          <span className="line-clamp-2">{row.insurer}</span>
                          <InsurancePolicyHoverCard
                            matter={matter}
                            insurance={row.insurance}
                            partyLabel={row.party}
                            partyTab={row.partyTab}
                            limitLabel={limitLabel}
                          />
                        </div>
                        <div className="mt-1 flex min-w-0 items-center gap-1 text-[9px] font-medium">
                          <span>{row.insurance.type} insurance</span>
                          {hasUpdateInsurancePermission ? (
                            <TooltipWrapper message="Edit insurance">
                              <Button
                                type="button"
                                variant="ghost"
                                className="text-primary h-3 w-3 shrink-0 p-0 [&_svg]:size-3"
                                aria-label={`Edit ${row.insurer} ${row.insurance.type} insurance for ${row.insured}`}
                                onClick={() => {
                                  openInsuranceModal({
                                    matterId: matter.id,
                                    insuranceId: row.insurance.id,
                                    editing: true,
                                    initialTab: "details",
                                  });
                                }}
                              >
                                <Pencil aria-hidden="true" />
                              </Button>
                            </TooltipWrapper>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-1 py-3 align-top">
                        <div className="space-y-1">
                          {row.limitLines.map((line, index) => (
                            <p
                              key={`${line.label ?? "limit"}:${String(index)}`}
                              className="font-semibold"
                            >
                              {line.value}
                              {line.label ? (
                                <span className="text-muted-foreground ml-1 block text-[9px] font-medium">
                                  {line.label}
                                </span>
                              ) : null}
                            </p>
                          ))}
                        </div>
                      </td>
                      <td className="px-1 py-3 align-top">
                        <StatusValue
                          label={coverage}
                          className={getStatusToneClass(coverage)}
                        />
                      </td>
                      <td className="px-1 py-3 align-top">
                        <StatusValue
                          label={liability}
                          className={getLiabilityToneClass(
                            row.insurance.liability,
                          )}
                        />
                      </td>
                    </tr>
                  );
                }),
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {rows.length === 0 && noInsuranceMarkers.length === 0 ? (
        <p className="text-muted-foreground mt-1 text-sm">No policies</p>
      ) : null}
    </section>
  );
}
